import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getRootUrl } from '@share-a-cart/shared-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useNow } from 'src/hooks';
import { getDeals } from '../api';
import {
  AMAZON_ITEM_RECHECK_MS,
  AMAZON_STORE_LABEL,
  AMAZON_STORE_SLUG,
  AmazonItemDealCard,
  DealCard,
  DealsNewsletterCard,
  DealsSeoLinks,
  amazonItemDealQuery,
  amazonItemDealsQuery,
  amazonItemPermalink,
  amazonItemTitle,
  asinFromItemSlug,
  goneCuratedDealPath,
  assignDealSlugs,
  dealListKey,
  dealPermalink,
  dealsPath,
  findDealBySlug,
  isAmazonStore,
  popularStores,
  showableAmazonItems,
  storeLabel,
  storeSlug,
} from '../components/deals';
import { SimpleSpinner } from '../components/common/SimpleSpinner';

const CART_PAGE_SOURCES = ['get', 'receive', 'make', 'share', 'embed', 'widget'];
const RELATED_LIMIT = 12;

const RelatedDeals = ({ heading, children }: { heading: string; children: ReactNode }) => (
  <div className='flex flex-col gap-3'>
    <h2 className='heading-300 text-primary-dark'>{heading}</h2>
    <div className='grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3'>{children}</div>
  </div>
);

export const DealDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { store: storeParam = '', deal: dealParam = '' } = useParams<{
    store: string;
    deal: string;
  }>();
  const fromCart = CART_PAGE_SOURCES.includes(searchParams.get('from') ?? '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [storeParam, dealParam]);

  // The shell's noindex meta is static HTML. Drop it on unmount so a client-side
  // navigation back to the store page is not left noindex.
  useEffect(() => {
    return () => {
      document.head.querySelectorAll('meta[name="robots"]').forEach((el) => {
        if (el.getAttribute('content')?.toLowerCase().includes('noindex')) el.remove();
      });
    };
  }, []);

  const dealsQuery = useQuery({
    queryKey: ['deals'],
    queryFn: getDeals,
    staleTime: 1000 * 60 * 10,
  });

  const allDeals = useMemo(() => dealsQuery.data ?? [], [dealsQuery.data]);
  const slugs = useMemo(() => assignDealSlugs(allDeals), [allDeals]);
  const seoStores = useMemo(() => popularStores(allDeals), [allDeals]);
  const deal = useMemo(
    () => findDealBySlug(allDeals, storeParam, dealParam),
    [allDeals, storeParam, dealParam],
  );

  const itemAsin = !deal && isAmazonStore(storeParam) ? asinFromItemSlug(dealParam) : null;
  const itemQuery = useQuery({ ...amazonItemDealQuery(itemAsin ?? ''), enabled: itemAsin != null });
  const otherItemsQuery = useQuery({ ...amazonItemDealsQuery, enabled: itemAsin != null });
  const now = useNow(AMAZON_ITEM_RECHECK_MS);
  const item = useMemo(
    () =>
      itemAsin && itemQuery.data && !itemQuery.isError
        ? (showableAmazonItems([itemQuery.data], now)[0] ?? null)
        : null,
    [itemAsin, itemQuery.data, itemQuery.isError, now],
  );
  const itemLoading = itemAsin != null && itemQuery.isLoading;
  const itemTitle = item ? amazonItemTitle(item) : null;

  const store = deal ? storeSlug(deal) : itemAsin ? AMAZON_STORE_SLUG : storeParam.toLowerCase();
  const label = deal ? storeLabel(deal) : itemAsin ? AMAZON_STORE_LABEL : storeParam;
  const dealSlug = deal ? slugs[allDeals.indexOf(deal)] : dealParam.toLowerCase();
  const permalink = dealPermalink(store, dealSlug);
  const canonical = `${getRootUrl()}${permalink}`;
  const canonicalPath = deal ? permalink : item ? amazonItemPermalink(item) : null;

  useEffect(() => {
    if (!dealsQuery.isSuccess) return;
    // Full page loads of a gone offer 302 on the server. This covers a shell that was
    // rendered before the feed caught up, and in-app visits to a slug that has dropped.
    // Unknown stores and collection slugs stay on the not-found view; the server 404s those.
    const target = goneCuratedDealPath(allDeals, storeParam, dealParam, searchParams);
    if (!target) return;
    navigate(target, { replace: true });
  }, [dealsQuery.isSuccess, allDeals, storeParam, dealParam, searchParams, navigate]);

  useEffect(() => {
    if (!dealsQuery.isSuccess || !itemAsin || !itemQuery.isSuccess || item) return;
    navigate(dealsPath(AMAZON_STORE_SLUG, searchParams), { replace: true });
  }, [dealsQuery.isSuccess, itemAsin, itemQuery.isSuccess, item, searchParams, navigate]);

  useEffect(() => {
    if (!dealsQuery.isSuccess || !canonicalPath) return;
    const current = `/deals/${encodeURIComponent(storeParam)}/${encodeURIComponent(dealParam)}`;
    if (current !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [dealsQuery.isSuccess, canonicalPath, dealParam, storeParam, navigate]);

  const related = useMemo(() => {
    if (!deal) return [];
    return allDeals
      .map((d, i) => ({ deal: d, path: dealPermalink(storeSlug(d), slugs[i]) }))
      .filter((row) => storeSlug(row.deal) === store && row.deal !== deal)
      .slice(0, RELATED_LIMIT);
  }, [allDeals, deal, slugs, store]);

  const relatedItems = useMemo(
    () =>
      itemAsin && otherItemsQuery.data && !otherItemsQuery.isError
        ? showableAmazonItems(otherItemsQuery.data, now)
            .filter((other) => other.asin !== itemAsin)
            .slice(0, RELATED_LIMIT)
        : [],
    [itemAsin, otherItemsQuery.data, otherItemsQuery.isError, now],
  );

  const dealName = deal?.name ?? itemTitle;
  const pageTitle = deal
    ? t('deals.deal_page_title', { deal: deal.name, store: label })
    : item
      ? t('deals.store_page_title', { store: label })
      : t('deals.page_title');
  const metaDescription = item
    ? t('deals.amazon.meta_description')
    : deal?.description?.trim()
      ? t('deals.deal_meta_description', {
          store: label,
          description: deal.description.trim().slice(0, 160),
        })
      : deal
        ? t('deals.deal_meta_description_short', { deal: deal.name, store: label })
        : t('deals.meta_description');

  const jsonLd = deal
    ? {
        '@context': 'https://schema.org',
        '@type': 'Offer',
        name: deal.name,
        description: deal.description || deal.name,
        url: canonical,
        seller: { '@type': 'Organization', name: label },
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name='description' content={metaDescription} />
        <meta name='robots' content='noindex, follow' />
        {jsonLd && (
          <script type='application/ld+json'>
            {JSON.stringify(jsonLd).replace(/</g, '\\u003c')}
          </script>
        )}
      </Helmet>

      <div className='relative flex w-full flex-col items-center gap-3 bg-primary-dark px-4 py-4'>
        {fromCart && (
          <button
            onClick={() => navigate(-1)}
            className='heading-100 inline-flex items-center self-start whitespace-nowrap text-skyBlue transition-all hover:text-white'
          >
            <ChevronLeftIcon className='-ml-1.5 -mr-0.5 -mt-0.5' />
            Back to Cart
          </button>
        )}
        <Link
          to={dealsPath(store)}
          className='heading-100 inline-flex items-center self-start whitespace-nowrap text-skyBlue transition-all hover:text-white'
        >
          <ChevronLeftIcon className='-ml-1.5 -mr-0.5 -mt-0.5' />
          {t('deals.back_to_store', { store: label })}
        </Link>
        <div className='flex max-w-3xl flex-col items-center gap-1 text-center'>
          <p className='font-poppins text-[11px] font-semibold uppercase tracking-widest text-blueGray'>
            {label}
          </p>
          <h1 className='heading-400 text-offWhite'>
            {item
              ? itemTitle
              : dealsQuery.isLoading || itemLoading
                ? '\u00a0'
                : (dealName ?? t('deals.deal_not_found'))}
          </h1>
          {deal?.description && <p className='text-200 text-blueGray'>{deal.description}</p>}
        </div>
      </div>

      <div className='mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-6'>
        {item ? (
          <>
            <div className='mx-auto w-full max-w-xl'>
              <AmazonItemDealCard item={item} expanded />
            </div>
            {relatedItems.length > 0 && (
              <RelatedDeals heading={t('deals.more_from_store', { store: label })}>
                {relatedItems.map((other) => (
                  <AmazonItemDealCard key={`amazon:${other.asin}`} item={other} />
                ))}
              </RelatedDeals>
            )}
          </>
        ) : dealsQuery.isLoading || itemLoading ? (
          <div className='mx-auto py-20'>
            <SimpleSpinner customWidth='w-16' customHeight='h-16' customPosition='block' />
          </div>
        ) : dealsQuery.isError || (itemAsin && itemQuery.isError) ? (
          <p className='text-200 py-20 text-center text-offGray'>
            Something went wrong loading deals. Please try again.
          </p>
        ) : !deal ? (
          <p className='text-200 py-20 text-center text-offGray'>{t('deals.deal_not_found')}</p>
        ) : (
          <>
            <div className='mx-auto w-full max-w-xl'>
              <DealCard deal={deal} path={permalink} expanded />
            </div>
            {related.length > 0 && (
              <RelatedDeals heading={t('deals.more_from_store', { store: label })}>
                {related.map((row, index) => (
                  <DealCard
                    key={dealListKey(row.deal, row.path, index)}
                    deal={row.deal}
                    path={row.path}
                  />
                ))}
              </RelatedDeals>
            )}
          </>
        )}
        <DealsSeoLinks currentSlug={store} stores={seoStores} />
      </div>

      <DealsNewsletterCard />
    </>
  );
};
